import React, { ComponentType, useEffect, useRef } from 'react';
import { createRoot, Root } from 'react-dom/client';

import { getPageCanvasBaseSize, PDF_SCALE } from './usePdfRenderer';

export interface RectangleOverlay {
  pageNumber: number;
  x: number;
  y: number;
  width: number;
  height: number;
  color: string;
  borderColor: string;
  sectionUuid: string;
}

type PageElementsMap = Map<number, HTMLDivElement>;

const BORDER_RADIUS = 8;
const STROKE_OPACITY = 1.0;
const STROKE_WIDTH = 2;
const CTA_BOTTOM_OFFSET = 20;

type UsePdfOverlayProps = {
  pageElements: PageElementsMap;
  boundingBoxes: RectangleOverlay[];
  onRectangleClick: (sectionUuid: string) => void;
  selectedSectionUuid: string | null;
  scale: number;
  scrollToPage: (page: number) => void;
  continuedOnLabel: string;
  ctaIcon?: ComponentType<{ size?: number }>;
};

export function usePdfOverlay({
  pageElements,
  boundingBoxes,
  onRectangleClick,
  selectedSectionUuid,
  scale = PDF_SCALE,
  scrollToPage,
  continuedOnLabel,
  ctaIcon,
}: UsePdfOverlayProps) {
  const overlaysRef = useRef<Map<number, HTMLCanvasElement>>(new Map());
  const boundingBoxesRef = useRef<RectangleOverlay[]>(boundingBoxes);
  const selectedSectionUuidRef = useRef<string | null>(selectedSectionUuid);
  const scaleRef = useRef(scale);
  const scrollToPageRef = useRef(scrollToPage);
  const ctaBannersRef = useRef<CtaBanner[]>([]);

  // Keep boundingBoxes ref up to date
  useEffect(() => {
    boundingBoxesRef.current = boundingBoxes;
  }, [boundingBoxes]);

  // Keep scale ref up to date so overlays created after a zoom start at the right size
  useEffect(() => {
    scaleRef.current = scale;
  }, [scale]);

  // Keep scrollToPage ref up to date so its identity does not recreate the CTA banners
  useEffect(() => {
    scrollToPageRef.current = scrollToPage;
  }, [scrollToPage]);

  // Keep selectedSectionUuid ref up to date
  useEffect(() => {
    selectedSectionUuidRef.current = selectedSectionUuid;
  }, [selectedSectionUuid]);

  // Initial render: create overlay canvases once
  useEffect(() => {
    if (pageElements.size === 0) return;

    const boxesByPage = groupBoundingBoxesByPage(boundingBoxesRef.current);
    const newOverlays = new Map<number, HTMLCanvasElement>();

    pageElements.forEach((pageElement, pageNumber) => {
      const boxes = boxesByPage.get(pageNumber);
      if (!boxes || boxes.length === 0) return;

      const pdfCanvas = pageElement.querySelector('canvas');
      if (!pdfCanvas) return;

      // Create overlay canvas at base scale
      const overlay = createOverlayCanvasElement(pdfCanvas);

      // Draw bounding boxes at base scale (with current selection if any)
      drawAllBoundingBoxes(
        overlay,
        boxes,
        selectedSectionUuidRef.current,
        PDF_SCALE,
      );

      // Attach click handler
      attachClickHandler(overlay, boxes, onRectangleClick, PDF_SCALE);

      applyScaleTransform(overlay, scaleRef.current);

      pageElement.appendChild(overlay);
      newOverlays.set(pageNumber, overlay);
    });

    overlaysRef.current = newOverlays;

    return () => {
      // Cleanup: remove all overlays
      newOverlays.forEach((overlay) => overlay.remove());
      overlaysRef.current.clear();
    };
    // Only recreate overlays when pageElements or onRectangleClick changes
  }, [pageElements, onRectangleClick]);

  // Redraw when the selection or the boxes' colors change
  useEffect(() => {
    if (overlaysRef.current.size === 0) return;

    const boxesByPage = groupBoundingBoxesByPage(boundingBoxes);

    overlaysRef.current.forEach((overlay, pageNumber) => {
      const boxes = boxesByPage.get(pageNumber);
      if (!boxes) return;

      // Clear and redraw with updated selection
      const ctx = overlay.getContext('2d');
      if (ctx) {
        ctx.clearRect(0, 0, overlay.width, overlay.height);
        drawAllBoundingBoxes(overlay, boxes, selectedSectionUuid, PDF_SCALE);
      }
    });
  }, [selectedSectionUuid, boundingBoxes]);

  // Apply CSS transform to overlays when scale changes
  useEffect(() => {
    overlaysRef.current.forEach((overlay) =>
      applyScaleTransform(overlay, scale),
    );
  }, [scale]);

  // Render multi-page CTA banners for the selected section
  useEffect(() => {
    removeCtaBanners(ctaBannersRef.current);
    ctaBannersRef.current = [];

    if (pageElements.size === 0 || !selectedSectionUuid) return;

    const transitions = findMultiPageTransitions(
      boundingBoxesRef.current,
    ).filter(({ box }) => box.sectionUuid === selectedSectionUuid);

    transitions.forEach(({ box, nextPage }) => {
      const pageElement = pageElements.get(box.pageNumber);
      if (!pageElement) return;

      const banner = createCtaBanner(continuedOnLabel, ctaIcon, () =>
        scrollToPageRef.current(nextPage),
      );
      pageElement.appendChild(banner.element);
      ctaBannersRef.current.push(banner);
    });

    return () => {
      removeCtaBanners(ctaBannersRef.current);
      ctaBannersRef.current = [];
    };
  }, [pageElements, selectedSectionUuid, continuedOnLabel, ctaIcon]);
}

function applyScaleTransform(overlay: HTMLCanvasElement, scale: number): void {
  overlay.style.transform = `scale(${scale / PDF_SCALE})`;
  overlay.style.transformOrigin = 'top left';
}

/**
 * Groups bounding boxes by their page number for efficient rendering.
 */
function groupBoundingBoxesByPage(
  boundingBoxes: RectangleOverlay[],
): Map<number, RectangleOverlay[]> {
  const boxesByPage = new Map<number, RectangleOverlay[]>();

  boundingBoxes.forEach((box) => {
    const existingBoxes = boxesByPage.get(box.pageNumber) || [];
    existingBoxes.push(box);
    boxesByPage.set(box.pageNumber, existingBoxes);
  });

  return boxesByPage;
}

interface MultiPageTransition {
  box: RectangleOverlay;
  nextPage: number;
}

/**
 * Finds page transitions for sections that span multiple pages.
 * Sections where all boxes are on the same page are excluded.
 */
function findMultiPageTransitions(
  boundingBoxes: RectangleOverlay[],
): MultiPageTransition[] {
  const bottomBoxByPage = new Map<string, Map<number, RectangleOverlay>>();

  boundingBoxes.forEach((box) => {
    const pageMap =
      bottomBoxByPage.get(box.sectionUuid) ??
      new Map<number, RectangleOverlay>();
    const existing = pageMap.get(box.pageNumber);
    if (!existing || box.y + box.height > existing.y + existing.height) {
      pageMap.set(box.pageNumber, box);
    }
    bottomBoxByPage.set(box.sectionUuid, pageMap);
  });

  const transitions: MultiPageTransition[] = [];

  bottomBoxByPage.forEach((pageMap) => {
    if (pageMap.size <= 1) return;

    const sorted = [...pageMap.values()].sort(
      (a, b) => a.pageNumber - b.pageNumber,
    );

    for (let i = 0; i < sorted.length - 1; i++) {
      transitions.push({ box: sorted[i], nextPage: sorted[i + 1].pageNumber });
    }
  });

  return transitions;
}

/**
 * Draws all bounding boxes on the given canvas overlay.
 */
function drawAllBoundingBoxes(
  canvas: HTMLCanvasElement,
  boxes: RectangleOverlay[],
  selectedSectionUuid: string | null,
  scale: number,
): void {
  boxes.forEach((box) => {
    const isSelected = box.sectionUuid === selectedSectionUuid;
    drawBoundingBox(canvas, box, isSelected, scale);
  });
}

/**
 * Creates an overlay canvas element with the same dimensions as the PDF canvas.
 */
function createOverlayCanvasElement(
  pdfCanvas: HTMLCanvasElement,
): HTMLCanvasElement {
  const { width, height } = getPageCanvasBaseSize(pdfCanvas);
  const overlay = document.createElement('canvas');
  // Same resolution as the page underneath; boxes are still drawn in base scale coordinates
  overlay.width = pdfCanvas.width;
  overlay.height = pdfCanvas.height;
  overlay.dataset.baseWidth = String(width);
  overlay.dataset.baseHeight = String(height);
  const outputScale = pdfCanvas.width / width;
  overlay.getContext('2d')?.setTransform(outputScale, 0, 0, outputScale, 0, 0);
  overlay.className = 'absolute top-0 left-0';
  overlay.style.width = `${width}px`;
  overlay.style.height = `${height}px`;
  overlay.style.cursor = 'pointer';
  overlay.style.pointerEvents = 'auto';

  return overlay;
}

/**
 * Attaches a click event handler that detects which bounding box was clicked.
 */
function attachClickHandler(
  overlay: HTMLCanvasElement,
  boxes: RectangleOverlay[],
  onRectangleClick: (sectionUuid: string) => void,
  scale: number,
): void {
  overlay.addEventListener('click', (event) => {
    const clickCoordinates = getCanvasClickCoordinates(overlay, event);
    const clickedBox = findClickedBoundingBox(boxes, clickCoordinates, scale);

    if (clickedBox) {
      onRectangleClick(clickedBox.sectionUuid);
    }
  });
}

/**
 * Converts browser click coordinates to canvas coordinates, accounting for scaling.
 */
function getCanvasClickCoordinates(
  canvas: HTMLCanvasElement,
  event: MouseEvent,
): { x: number; y: number } {
  const rect = canvas.getBoundingClientRect();
  const { width, height } = getPageCanvasBaseSize(canvas);
  const scaleX = width / rect.width;
  const scaleY = height / rect.height;
  const x = (event.clientX - rect.left) * scaleX;
  const y = (event.clientY - rect.top) * scaleY;

  return { x, y };
}

/**
 * Finds which bounding box (if any) contains the click coordinates.
 */
function findClickedBoundingBox(
  boxes: RectangleOverlay[],
  clickCoordinates: { x: number; y: number },
  scale: number,
): RectangleOverlay | null {
  for (const box of boxes) {
    if (isPointInsideBoundingBox(clickCoordinates, box, scale)) {
      return box;
    }
  }

  return null;
}

/**
 * Checks if a point is inside a bounding box.
 */
function isPointInsideBoundingBox(
  point: { x: number; y: number },
  box: RectangleOverlay,
  scale: number,
): boolean {
  const scaledX = box.x * scale;
  const scaledY = box.y * scale;
  const scaledWidth = box.width * scale;
  const scaledHeight = box.height * scale;

  return (
    point.x >= scaledX &&
    point.x <= scaledX + scaledWidth &&
    point.y >= scaledY &&
    point.y <= scaledY + scaledHeight
  );
}

function drawBoundingBox(
  canvas: HTMLCanvasElement,
  box: RectangleOverlay,
  isSelected: boolean,
  scale: number,
): void {
  const ctx = canvas.getContext('2d');
  if (!ctx) return;
  drawRoundedRectangle(ctx, box, scale);
  fillWithColor(ctx, box.color);
  if (isSelected) {
    strokeWithBorder(ctx, box.borderColor);
  }
}

function drawRoundedRectangle(
  ctx: CanvasRenderingContext2D,
  box: RectangleOverlay,
  scale: number,
): void {
  ctx.beginPath();
  ctx.roundRect(
    box.x * scale,
    box.y * scale,
    box.width * scale,
    box.height * scale,
    BORDER_RADIUS,
  );
}

function fillWithColor(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.fillStyle = color;
  ctx.fill();
}

function strokeWithBorder(ctx: CanvasRenderingContext2D, color: string): void {
  ctx.globalAlpha = STROKE_OPACITY;
  ctx.strokeStyle = color;
  ctx.lineWidth = STROKE_WIDTH;
  ctx.stroke();
}

interface CtaBanner {
  element: HTMLDivElement;
  iconRoot: Root | null;
}

function removeCtaBanners(banners: CtaBanner[]): void {
  banners.forEach(({ element, iconRoot }) => {
    element.remove();
    // Deferred: a root cannot be unmounted synchronously while React is rendering
    if (iconRoot) queueMicrotask(() => iconRoot.unmount());
  });
}

function createCtaBanner(
  label: string,
  icon: ComponentType<{ size?: number }> | undefined,
  onClick: () => void,
): CtaBanner {
  let iconRoot: Root | null = null;
  const banner = document.createElement('div');

  banner.style.position = 'absolute';
  banner.style.bottom = `${CTA_BOTTOM_OFFSET}px`;
  banner.style.left = '50%';
  banner.style.transform = 'translateX(-50%)';
  banner.style.zIndex = '10';
  banner.style.cursor = 'pointer';
  banner.style.pointerEvents = 'auto';
  banner.style.display = 'inline-flex';
  banner.style.height = '36px';
  banner.style.justifyContent = 'center';
  banner.style.alignItems = 'center';
  banner.style.gap = '10px';
  banner.style.padding = '8px 16px';
  banner.style.borderRadius = '8px';
  banner.style.backgroundColor = '#F3DED8';
  banner.style.boxShadow =
    '0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)';
  banner.style.fontFamily = 'inherit';
  banner.style.whiteSpace = 'nowrap';
  banner.style.userSelect = 'none';
  banner.style.transition =
    'transform 0.15s ease, box-shadow 0.15s ease, background-color 0.15s ease';

  const text = document.createElement('span');
  text.textContent = label;
  banner.appendChild(text);

  if (icon) {
    const iconContainer = document.createElement('span');
    iconContainer.style.display = 'flex';
    iconContainer.style.alignItems = 'center';
    iconContainer.style.flexShrink = '0';
    banner.appendChild(iconContainer);
    iconRoot = createRoot(iconContainer);
    iconRoot.render(React.createElement(icon, { size: 16 }));
  }

  banner.addEventListener('mouseenter', () => {
    banner.style.transform = 'translateX(-50%) translateY(-2px)';
    banner.style.backgroundColor = '#eecfc7';
    banner.style.boxShadow =
      '0 20px 25px -5px rgba(0,0,0,0.12), 0 8px 10px -6px rgba(0,0,0,0.12)';
  });
  banner.addEventListener('mouseleave', () => {
    banner.style.transform = 'translateX(-50%)';
    banner.style.backgroundColor = '#F3DED8';
    banner.style.boxShadow =
      '0 10px 15px -3px rgba(0,0,0,0.10), 0 4px 6px -4px rgba(0,0,0,0.10)';
  });
  banner.addEventListener('click', (e) => {
    e.stopPropagation();
    onClick();
  });

  return { element: banner, iconRoot };
}
